import { useEffect, useMemo, useState } from 'react'
import { getCaseMeshes, downloadCaseMesh, getReportMeta } from '../services/api'
import { renderReport, REPORT_LOOK } from '../lib/meshRenderer'
import { buildSseReport, hasDisplayableCtData } from '../lib/sseReport'
import { buildViewsImage, buildSingleViewImage, IMAGE_BG } from '../lib/viewsImage'
import ReportLookPanel, { useReportLook, STAMP_RELIEF, STAMP_RELIEF_MARKED } from '../components/ReportLookPanel'

// SSE report view set (Page 1 grid = the 6 ortho; ISO used on Page 2).
const ORTHO = ['A', 'P', 'LL', 'RL', 'S', 'I']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const fmtDate = (iso) => { const p = (iso || '').split('-'); return p[1] ? `${p[0]}-${MONTHS[+p[1] - 1]}-${p[2]}` : (iso || '') }
const VIEW_LABELS = { A: 'Anterior', P: 'Posterior', LL: 'Left Lateral', RL: 'Right Lateral', S: 'Superior', I: 'Inferior', ISO: 'Isometric' }

// Official Osteo3d OBJ-group colours (osteo3d-color-codes).
// Palette offered in the picker. Slot 0 is the default bone tone — every group
// starts there, so an untouched model renders exactly as it would with no groups.
const BONE_TONE = '#CFC1A7'
const DEFAULT_COLORS = ['#CFC1A7', '#FF7276', '#97F6F6', '#B2EE91', '#FF8F8F']

function parseObjGroups(text) {
  const names = [], seen = new Set()
  for (const line of text.split('\n')) {
    const m = /^\s*[og]\s+(.+?)\s*$/.exec(line)
    if (m && !seen.has(m[1])) { seen.add(m[1]); names.push(m[1]) }
  }
  return names
}

export default function PreviewPage() {
  const [mode, setMode] = useState('upload') // 'upload' | 'case'
  const [file, setFile] = useState(null)
  const [objGroups, setObjGroups] = useState([]) // [{ name, color }]
  // Which OBJ group is the engraved case ID, or '' for none. The operator says so
  // because the shape cannot: a stamp on a curved skull is measurably LESS plate-like
  // than a cutting guide, so every automatic rule tried either missed the stamp or lit
  // an appliance. See `stampGroup` in meshRenderer.js.
  const [stampGroup, setStampGroup] = useState('')

  const [caseId, setCaseId] = useState('')
  const [meshes, setMeshes] = useState([])
  const [meshFileId, setMeshFileId] = useState('')
  const [meshesLoading, setMeshesLoading] = useState(false)

  // Every look control — bone tone, shading, finish, background, render style,
  // matcap brightness, shadow strength, flip A/P — comes from the shared panel.
  // The Case Report Builder mounts the same one, so the two tools cannot drift.
  const reportLook = useReportLook()
  const { look } = reportLook   // page1Title and the black-background preview styling

  const [info, setInfo] = useState({
    caseId: '', patientName: '', surgeonName: '', hospital: '', date: '',
    caseNote: '', otherNotes: '', waiverOverride: null, waiverNotes: '', note: '',
  })
  const setI = (k, v) => setInfo((p) => ({ ...p, [k]: v }))
  const [ctInfo, setCtInfo] = useState({})
  // What the CT block's values ACTUALLY rest on: SSE's record of the series the
  // operator reconstructed, or the portal's own re-derivation. The report used to
  // print the portal's guess with no indication anything was uncertain — which is how
  // O3D-2026-08-01-0004 shipped naming a series nobody had chosen.
  const [ctSource, setCtSource] = useState(null)
  const [imaging, setImaging] = useState({})

  const [layout, setLayout] = useState('grid') // 'grid' | 'pages'
  const [includeModelProcess, setIncludeModelProcess] = useState(false) // Page 3 (mesh metrics + imaging) — off by default
  const [includeCaseInfo, setIncludeCaseInfo] = useState(true)  // Page 2 (case notes + CT data + ISO) — on by default
  // idle | loading | ok | empty | error. The team hit 'empty' and could not tell it
  // apart from 'I typed the Case ID wrong', so they retyped the whole scan by hand.
  const [metaStatus, setMetaStatus] = useState('idle')
  const [selViews, setSelViews] = useState(new Set([...ORTHO, 'ISO']))

  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState(null) // { views, ortho, iso, labels, metrics }
  const [meshBytes, setMeshBytes] = useState(null) // last-rendered mesh (for image re-render)
  const [meshFilename, setMeshFilename] = useState('')

  const [logo, setLogo] = useState(null)
  const [logoAspect, setLogoAspect] = useState(3)

  useEffect(() => {
    fetch('/osteo3d-logo.png').then((r) => r.blob()).then((b) => {
      const fr = new FileReader()
      fr.onload = () => {
        setLogo(fr.result)
        const im = new Image(); im.onload = () => setLogoAspect(im.naturalWidth / im.naturalHeight || 3); im.src = fr.result
      }
      fr.readAsDataURL(b)
    }).catch(() => {})
  }, [])

  async function handleFile(f) {
    setFile(f); setObjGroups([]); setStampGroup('')
    if (f && /\.obj$/i.test(f.name)) {
      const names = parseObjGroups(await f.text())
      // NO automatic colours. Every group starts at the bone tone; the operator
      // opts in per group. Auto-assigning by FILE ORDER (DEFAULT_COLORS[i % 5])
      // meant the colours implied an anatomical distinction they never carried —
      // group 1 also got the bone tone, so it looked "uncoloured", and a 6th group
      // silently wrapped back to bone. On a surgical model, colour that means
      // nothing is worse than no colour: the surgeon assumes it marks something.
      if (names.length > 1) setObjGroups(names.map((name) => ({ name, color: BONE_TONE })))
    }
  }

  // A stamp marked in Upload mode must not survive into From-case. The panel that shows
  // and clears it is upload-only, but generate() still sends the name, so a stale value
  // that happened to match a Drive mesh would light it invisibly.
  useEffect(() => { setStampGroup('') }, [mode])

  // Marking a stamp turns the relief on; clearing it turns it back off.
  //
  // ONE effect keyed on the value, rather than a setStampRelief beside each of the four
  // places stampGroup changes. A per-site copy is precisely how a per-item flag got dropped
  // at three separate layers in this renderer — the rule out of that was to act at the
  // DESTINATION, not at each source. It also means the operator's own slider adjustment
  // survives until they mark a different group, which is what they would expect.
  useEffect(() => {
    reportLook.setStampRelief(stampGroup ? STAMP_RELIEF_MARKED : STAMP_RELIEF.default)
  }, [stampGroup, reportLook.setStampRelief])

  // From-case: list the case's meshes on Drive (mesh source only).
  useEffect(() => {
    if (mode !== 'case' || caseId.length < 8) {
      setMeshes([]); setMeshFileId('')
      // Also clear info.caseId. `setI('caseId', caseId)` lives AFTER this early
      // return, so in From-case mode clearing the box never shortened info.caseId,
      // the metadata effect never re-ran, and the operator was left looking at an
      // empty Case ID box under "DICOM metadata loaded from case ✓" — with the
      // previous case's scan still in state, printing on the report and driving the
      // automatic waiver.
      if (mode === 'case') setI('caseId', '')
      return
    }
    setMeshesLoading(true)
    const t = setTimeout(() => {
      getCaseMeshes(caseId).then((r) => setMeshes(r.data.meshes || [])).catch(() => setMeshes([])).finally(() => setMeshesLoading(false))
      setI('caseId', caseId)
    }, 450)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, caseId])

  // Fetch the DICOM metadata (report pages 2-3) whenever a Case ID is set — works in
  // BOTH Upload-file and From-case modes (Upload = STL for renders + Case ID for meta).
  useEffect(() => {
    const cid = (info.caseId || '').trim()
    if (cid.length < 8) {
      // Clearing or shortening the Case ID must DROP the metadata, not just hide the
      // banner. Leaving it behind meant the previous case's CT data stayed in state,
      // printed onto this report, and drove the automatic quality waiver — wrong
      // patient's scan on a surgical document.
      setCtInfo({}); setImaging({}); setMetaStatus('idle')
      return
    }
    // Drop the previous case's data BEFORE fetching, not after. Otherwise the
    // header prints the new Case ID for the whole debounce + round trip while
    // ctInfo still holds the old scan — reachable by correcting a typo and hitting
    // Download inside that window.
    setCtInfo({}); setImaging({}); setCtSource(null)
    setMetaStatus('loading')
    // Guards against last-response-wins: the cleanup only cancels the debounce, so
    // without this a slow reply for case A could land after case B and overwrite it.
    let cancelled = false
    const t = setTimeout(() => {
      getReportMeta(cid).then((r) => {
        if (cancelled) return
        const ct = r.data.ct_info || {}
        setCtInfo(ct)
        setImaging(r.data.imaging || {})
        setCtSource(r.data.sse || null)
        if (ct.study_date && !info.date) setI('date', ct.study_date)
        // The backend answers 200 with EMPTY objects when the case has no upload or
        // no computable scan — a success that carries nothing. Saying so is the
        // difference between "this case has no DICOM" and "I mistyped the Case ID".
        //
        // Uses the SAME predicate the PDF uses, so the banner can never promise a
        // CT block the report then omits.
        setMetaStatus(hasDisplayableCtData(ct) ? 'ok' : 'empty')
      }).catch(() => {
        if (cancelled) return
        setCtInfo({}); setImaging({}); setCtSource(null); setMetaStatus('error')
      })
    }, 500)
    return () => { cancelled = true; clearTimeout(t) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info.caseId])

  const selectedMesh = useMemo(() => meshes.find((m) => m.id === meshFileId), [meshes, meshFileId])

  // Quality waiver: auto when study > 30 days old, unless overridden.
  const studyAgeDays = ctInfo.study_age_days
  const waiverAuto = typeof studyAgeDays === 'number' && studyAgeDays > 30
  const waiverEffective = info.waiverOverride == null ? waiverAuto : info.waiverOverride

  function toggleView(code) {
    setSelViews((s) => { const n = new Set(s); n.has(code) ? n.delete(code) : n.add(code); return n })
  }

  async function generate() {
    setError(''); setResult(null); setBusy(true)
    try {
      const colors = objGroups.length ? Object.fromEntries(objGroups.map((g) => [g.name, g.color])) : null
      // REPORT_LOOK is the shared definition (meshRenderer.js); `renderOpts` is
      // the operator's panel on top of it. Values that are not operator-driven —
      // smoothNormals, cameraRelativeKey, labelRelief — come from there rather
      // than being repeated here, so the Case Report cannot drift away again.
      const opts = { ...REPORT_LOOK, ...reportLook.renderOpts, colors, stampGroup: stampGroup || null }
      let bytes, filename
      if (mode === 'upload') {
        if (!file) { setError('Choose an STL, OBJ, or GLB.'); setBusy(false); return }
        bytes = await file.arrayBuffer(); filename = file.name
      } else {
        if (!meshFileId) { setError('Pick an STL/OBJ from the case.'); setBusy(false); return }
        const r = await downloadCaseMesh(caseId, meshFileId); bytes = r.data; filename = selectedMesh?.name
      }
      setMeshBytes(bytes); setMeshFilename(filename)
      const data = await renderReport(bytes, { ...opts, filename })
      setResult(data)
    } catch (e) {
      setError(e.response?.data?.error || e.message)
    } finally { setBusy(false) }
  }

  async function downloadImage() {
    if (!meshBytes) { setError('Render first.'); return }
    setError(''); setBusy(true)
    try {
      const colors = objGroups.length ? Object.fromEntries(objGroups.map((g) => [g.name, g.color])) : null
      // REPORT_LOOK first, exactly as generate() does. This option set used to be
      // hand-written, which meant the downloaded PNGs rendered WITHOUT smoothNormals
      // and cameraRelativeKey while the PDF of the same mesh rendered with them — two
      // different images of one model, from one click apart.
      const data = await renderReport(meshBytes, {
        ...REPORT_LOOK, ...reportLook.renderOpts, stampGroup: stampGroup || null,
        filename: meshFilename, views: ORTHO, cornerLabels: false, watermark: false,
        // The composite sheet paints its own dark ground; the panel's white/black
        // choice does not apply to it.
        background: IMAGE_BG, colors,
      })
      const today = new Date()
      const blob = await buildViewsImage({
        views: data.views,
        date: info.date ? fmtDate(info.date) : `${today.getFullYear()}-${MONTHS[today.getMonth()]}-${String(today.getDate()).padStart(2, '0')}`,
        surgeonName: info.surgeonName, caseId: info.caseId, note: info.note,
      })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob); a.download = `${info.caseId || 'views'}.png`; a.click()
      URL.revokeObjectURL(a.href)
    } catch (e) { setError(e.message) } finally { setBusy(false) }
  }

  // Individual single-view PNGs — one file per selected view (alongside the composite).
  async function downloadPerViewImages() {
    if (!meshBytes) { setError('Render first.'); return }
    const codes = [...ORTHO, 'ISO'].filter((c) => selViews.has(c))
    if (!codes.length) { setError('Select at least one view.'); return }
    setError(''); setBusy(true)
    try {
      const colors = objGroups.length ? Object.fromEntries(objGroups.map((g) => [g.name, g.color])) : null
      // REPORT_LOOK first — see downloadImage() above for why.
      const data = await renderReport(meshBytes, {
        ...REPORT_LOOK, ...reportLook.renderOpts, stampGroup: stampGroup || null,
        filename: meshFilename, views: codes, cornerLabels: false, watermark: false,
        background: IMAGE_BG, colors,
      })
      const today = new Date()
      const dstr = info.date ? fmtDate(info.date) : `${today.getFullYear()}-${MONTHS[today.getMonth()]}-${String(today.getDate()).padStart(2, '0')}`
      for (let i = 0; i < codes.length; i++) {
        const code = codes[i]
        const blob = await buildSingleViewImage({
          render: data.views[code], code, label: VIEW_LABELS[code], index: i + 1,
          date: dstr, surgeonName: info.surgeonName, caseId: info.caseId, note: info.note,
        })
        const a = document.createElement('a')
        a.href = URL.createObjectURL(blob); a.download = `${info.caseId || 'view'}_a${i + 1}_${code}.png`; a.click()
        await new Promise((r) => setTimeout(r, 350)) // stagger so the browser keeps all downloads
        URL.revokeObjectURL(a.href)
      }
    } catch (e) { setError(e.message) } finally { setBusy(false) }
  }

  async function downloadPdf() {
    if (!result) return
    setBusy(true)
    try {
      // Option 2 = one selected view per page; Option 1 = the 6-view grid.
      let ortho = result.ortho, orthoCodes = ORTHO
      if (layout === 'pages') {
        const codes = [...ORTHO, 'ISO'].filter((c) => selViews.has(c))
        ortho = codes.map((c) => result.views[c]).filter(Boolean)
        orthoCodes = codes
      }
      const doc = await buildSseReport({
        caseId: info.caseId, patientName: info.patientName, surgeonName: info.surgeonName,
        hospital: info.hospital, date: info.date, caseNote: info.caseNote, otherNotes: info.otherNotes,
        page1Title: look.page1Title, qualityWaiver: waiverEffective, waiverNotes: info.waiverNotes, note: info.note,
        ctInfo, imaging, meshMetrics: result.metrics, processSteps: '', includeModelProcess, includeCaseInfo,
        // Where the CT block's values came from. The PDF footnotes it, so a reader
        // can tell "the operator confirmed this series" from "we guessed".
        ctSource,
        logo, logoAspect, ortho, orthoCodes, iso: result.iso, layout,
        dateStr: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }).replace(/ /g, '-'),
      })
      doc.save(`${info.caseId || 'CASE'}_report.pdf`)
    } catch (e) { setError(e.message) } finally { setBusy(false) }
  }

  const field = 'w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm'
  const lbl = 'block text-xs font-medium text-gray-500 mb-1'

  return (
    <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
      {/* Controls */}
      <div className="space-y-4">
        <div className="card space-y-4">
          <div className="flex gap-2">
            <button onClick={() => setMode('upload')} className={mode === 'upload' ? 'btn-primary flex-1' : 'btn-secondary flex-1'}>Upload file</button>
            <button onClick={() => setMode('case')} className={mode === 'case' ? 'btn-primary flex-1' : 'btn-secondary flex-1'}>From case</button>
          </div>
          {mode === 'upload' ? (
            <div className="space-y-3">
              <div>
                <label className={lbl}>STL, OBJ, or GLB</label>
                <input type="file" accept=".stl,.obj,.glb,.gltf" onChange={(e) => handleFile(e.target.files?.[0] || null)}
                  className="block w-full text-sm text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-brand-50 file:text-brand-700 file:font-medium" />
              </div>
              <div>
                <label className={lbl}>Case ID <span className="text-gray-400 font-normal">(optional — pulls DICOM metadata)</span></label>
                <input value={info.caseId} placeholder="O3D-XXXXXXXX" onChange={(e) => setI('caseId', e.target.value.trim().toUpperCase())} className={field} />
                {metaStatus === 'loading' && <p className="text-xs text-gray-500 mt-1">Looking up DICOM metadata…</p>}
                {metaStatus === 'ok' && <p className="text-xs text-green-600 mt-1">DICOM metadata loaded ✓</p>}
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <label className={lbl}>Case ID</label>
                <input value={caseId} placeholder="O3D-XXXXXXXX" onChange={(e) => { setCaseId(e.target.value.trim().toUpperCase()); setMeshFileId('') }} className={field} />
              </div>
              <div>
                <label className={lbl}>STL / OBJ on Drive</label>
                {meshesLoading ? <p className="text-sm text-gray-400 py-2">Searching Drive…</p>
                  : caseId.length >= 8 && meshes.length === 0 ? <p className="text-sm text-amber-600 py-2">No STL or OBJ found on Drive for this case.</p>
                  : (
                    <select value={meshFileId} onChange={(e) => setMeshFileId(e.target.value)} disabled={!meshes.length} className={`${field} disabled:bg-gray-100`}>
                      <option value="">{meshes.length ? 'Select a mesh…' : 'Enter a Case ID above'}</option>
                      {meshes.map((m) => <option key={m.id} value={m.id}>{m.name}{m.size ? ` (${(m.size / 1048576).toFixed(0)}MB)` : ''}</option>)}
                    </select>
                  )}
                {/* Driven by metaStatus, not by poking at ctInfo: the old
                    `(ctInfo.scan_type || imaging.grade)` test kept showing a green
                    tick after the Case ID was cleared, contradicting the banner. */}
                {mode === 'case' && metaStatus === 'loading' && (
                  <p className="text-xs text-gray-500 mt-1">Looking up DICOM metadata…</p>
                )}
                {mode === 'case' && metaStatus === 'ok' && (
                  <p className="text-xs text-green-600 mt-1">DICOM metadata loaded from case ✓</p>
                )}
              </div>
            </div>
          )}
        </div>

        <ReportLookPanel value={reportLook} />

        {/* OBJ group colours */}
        {mode === 'upload' && objGroups.length > 0 && (
          <div className="card">
            <span className="text-sm font-semibold text-gray-700 block mb-2">Colours ({objGroups.length} groups)</span>
            <div className="space-y-1.5">
              {objGroups.map((g, i) => (
                <div key={g.name} className="flex items-center gap-2">
                  <input type="color" value={g.color} onChange={(e) => setObjGroups(objGroups.map((x, j) => j === i ? { ...x, color: e.target.value } : x))} className="w-7 h-7 rounded border border-gray-200 p-0" />
                  <span className="text-sm text-gray-600 truncate flex-1">{g.name}</span>
                  <label className="flex items-center gap-1 text-xs text-gray-500 shrink-0" title="Mark this group as the engraved case ID so it is lit and reads face-on">
                    <input type="radio" name="stampGroup" checked={stampGroup === g.name}
                      onChange={() => setStampGroup(g.name)} />
                    stamp
                  </label>
                </div>
              ))}
            </div>
            <div className="mt-2 border-t border-gray-100 pt-2 flex items-center justify-between">
              <span className="text-[11px] text-gray-500">
                An engraved case ID cannot show face-on under the matcap — a letter's top face
                shares the plate's normal. Mark its group and it is lit so the Posterior view reads.
              </span>
              {stampGroup && (
                <button type="button" onClick={() => setStampGroup('')}
                  className="text-xs text-brand-600 underline shrink-0 ml-2">clear</button>
              )}
            </div>
          </div>
        )}

        {/* Layout */}
        <div className="card">
          <span className="text-sm font-semibold text-gray-700 block mb-2">Layout</span>
          <label className="flex items-center gap-2 text-sm text-gray-600 mb-1"><input type="radio" checked={layout === 'grid'} onChange={() => setLayout('grid')} /> Option 1 — 6 views on one page</label>
          <label className="flex items-center gap-2 text-sm text-gray-600"><input type="radio" checked={layout === 'pages'} onChange={() => setLayout('pages')} /> Option 2 — one view per page</label>
          <div className="mt-2 border-t border-gray-100 pt-2">
            <span className="text-xs font-medium text-gray-500 block mb-1">Views <span className="text-gray-400">(Option 2 pages &amp; per-view images)</span></span>
            <div className="grid grid-cols-2 gap-1">
              {[...ORTHO, 'ISO'].map((c) => (
                <label key={c} className="flex items-center gap-2 text-xs text-gray-600"><input type="checkbox" checked={selViews.has(c)} onChange={() => toggleView(c)} /> {VIEW_LABELS[c]}</label>
              ))}
            </div>
          </div>
          {/* Both metadata pages are optional. Page 2 used to be unconditional, so
              unchecking the page-3 box still produced "the metadata page" — which is
              exactly what the team reported. */}
          <label className="flex items-center gap-2 text-sm text-gray-600 border-t border-gray-100 pt-2 mt-2">
            <input type="checkbox" checked={includeCaseInfo} onChange={(e) => setIncludeCaseInfo(e.target.checked)} /> Include Case Information page <span className="text-gray-400 text-xs">(case notes + CT data + isometric)</span>
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-600">
            <input type="checkbox" checked={includeModelProcess} onChange={(e) => setIncludeModelProcess(e.target.checked)} /> Include Model &amp; Process page <span className="text-gray-400 text-xs">(mesh metrics + imaging quality)</span>
          </label>
        </div>

        {/* Case / report fields */}
        <div className="card space-y-2">
          <span className="text-sm font-semibold text-gray-700 block">Report fields</span>
          {/* Say what the metadata lookup found. It answers 200 with nothing when a
              case has no DICOM, so silence here reads as "my Case ID is wrong" — the
              team retyped an entire scan by hand rather than know which it was. */}
          {metaStatus === 'empty' && (
            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
              No DICOM metadata found for this Case ID — the CT data section will be omitted.
            </p>
          )}
          {metaStatus === 'error' && (
            <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">
              Could not reach the metadata service — the CT data section will be omitted.
            </p>
          )}
          {/* No success notice here on purpose: the Case ID fields above already show
              "DICOM metadata loaded ✓". Success was never the silent case — only these
              two were, which is why the operator retyped a scan by hand. */}
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="Case ID" value={info.caseId} onChange={(e) => setI('caseId', e.target.value)} className={field} />
            <input type="date" value={info.date} onChange={(e) => setI('date', e.target.value)} className={field} />
            <input placeholder="Patient name" value={info.patientName} onChange={(e) => setI('patientName', e.target.value)} className={field} />
            <input placeholder="Surgeon name" value={info.surgeonName} onChange={(e) => setI('surgeonName', e.target.value)} className={field} />
            <input placeholder="Hospital" value={info.hospital} onChange={(e) => setI('hospital', e.target.value)} className={`${field} col-span-2`} />
          </div>
          <input placeholder="Additional note — e.g. Mandible will be printed separately" value={info.note} onChange={(e) => setI('note', e.target.value)} className={field} />
          <textarea placeholder="Case note (Page 2)" rows={2} value={info.caseNote} onChange={(e) => setI('caseNote', e.target.value)} className={`${field} resize-none`} />
          <div>
            <label className={lbl}>Quality waiver</label>
            <div className="flex items-center gap-2 text-xs">
              {[['Auto', null], ['Yes', true], ['No', false]].map(([label, val]) => (
                <button key={label} onClick={() => setI('waiverOverride', val)} className={`px-2.5 py-1 rounded ${info.waiverOverride === val ? 'btn-primary' : 'btn-secondary'}`}>{label}</button>
              ))}
              <span className="text-gray-500">{studyAgeDays != null ? `scan ${studyAgeDays}d — ` : ''}on report: <b className={waiverEffective ? 'text-amber-600' : 'text-gray-500'}>{waiverEffective ? 'Yes' : 'No'}</b></span>
            </div>
            {waiverEffective && <textarea placeholder="Waiver notes" rows={2} value={info.waiverNotes} onChange={(e) => setI('waiverNotes', e.target.value)} className={`${field} resize-none mt-2`} />}
          </div>
        </div>

        <button onClick={generate} disabled={busy} className="btn-primary w-full">{busy ? 'Rendering…' : 'Render views'}</button>
        {error && <p className="text-sm text-red-600">{error}</p>}
      </div>

      {/* Result */}
      <div className="card min-h-[400px]">
        {!result ? (
          <div className="h-full flex items-center justify-center text-gray-400 text-sm">{busy ? 'Rendering views…' : 'Rendered views will appear here.'}</div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-4 gap-2">
              <span className="text-sm font-semibold text-gray-700">Preview</span>
              <div className="flex flex-wrap gap-2 justify-end">
                <button onClick={downloadImage} disabled={busy} className="btn-secondary">Download image (6 views)</button>
                <button onClick={downloadPerViewImages} disabled={busy} className="btn-secondary">Download images (per view)</button>
                <button onClick={downloadPdf} disabled={busy} className="btn-primary">Download PDF report</button>
              </div>
            </div>
            {/* An un-applied stamp relief is INVISIBLE in the output — the sheet looks correct
                and the engraving is simply missing from the face-on view. Say so here, before
                the operator downloads it. */}
            {/* The outline was refused on every view that could see the stamp. Distinct from the
                banner below: relief DID apply, so the text is there — it just has no edge and
                will read faintly. Silent refusal is the failure this whole feature exists to
                stop, so it gets its own line rather than being folded into "may not read". */}
            {result.edge?.refused && (
              <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                <span className="font-semibold">The engraved text has no outline</span>{' '}
                because the characters render too small in the{' '}
                {VIEW_LABELS[result.edge.refusedView] || 'face-on'} view to carry one — a band
                that wide would fill them in rather than outline them. The text is still
                engraved; check it reads before sending this out.
              </div>
            )}
            {result.relief?.requested && !result.relief.applied && (
              <div className="mb-3 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                <span className="font-semibold">Engraved text may not read in the face-on view</span>
                {' '}(Posterior, and Inferior for a base stamp).{' '}
                {/* The marked-group cases name the group and say what to do. Before this they
                    fell through to the generic text, which told an operator who HAD marked a
                    group that nothing was marked — the same words they saw beforehand. */}
                {result.relief.reason === 'stamp-not-found' ? (
                  <>The group you marked (<span className="font-mono">{stampGroup}</span>) is not in
                  this file — it was probably renamed on re-export. Pick the stamp again from the
                  list above.</>
                ) : result.relief.reason === 'stamp-too-large' ? (
                  <>The group you marked (<span className="font-mono">{stampGroup}</span>) is too
                  large to be a stamp, so it was refused rather than lit as one. Pick the group
                  that holds only the engraving.</>
                ) : (
                  <>No engraved plate was found in this file and the self-shadow relief pass is
                  switched off, so nothing is revealing the lettering — check the Posterior view
                  before sending this out.</>
                )}
              </div>
            )}
            <div className={`grid grid-cols-2 md:grid-cols-3 gap-3 ${look.background === 'black' ? 'bg-gray-900 p-3 rounded-lg' : ''}`}>
              {[...ORTHO, 'ISO'].filter((c) => result.views[c]).map((c) => (
                <figure key={c} className="text-center">
                  <img src={`data:image/png;base64,${result.views[c]}`} alt={result.labels[c]} className="w-full rounded-lg border border-gray-100" />
                  <figcaption className={`text-xs mt-1 ${look.background === 'black' ? 'text-gray-300' : 'text-gray-500'}`}>{result.labels[c]}</figcaption>
                </figure>
              ))}
            </div>
            {result.metrics && (
              <p className="text-xs text-gray-400 mt-3">
                {result.metrics.faces?.toLocaleString()} faces · {result.metrics.vertices?.toLocaleString()} vertices ·
                bbox {result.metrics.bbox_mm?.map((e) => e.toFixed(0)).join('×')} mm · {result.metrics.volume_cm3?.toFixed(1)} cm³
              </p>
            )}
          </>
        )}
      </div>
    </div>
  )
}
